import { Icon } from "@circle-react-shared/Icon";
import { SuggestionMenuRow } from "@circle-react-shared/SuggestionMenu/SuggestionMenuRow";
import { Box } from "@circleco/compass/components/Box";
import { Typography } from "@circleco/compass/components/Typography";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const OPTION_ICON = (
  <Icon type="16-stars-ai" viewBox="0 0 20 16" className="h-4 w-5 shrink-0" />
);

export interface CompassPromptMenuHandle {
  moveDown: () => void;
  moveUp: () => void;
  selectActive: () => void;
}

export interface CompassPromptMenuOption {
  id: string;
  label: string;
}

interface CompassPromptMenuProps {
  label?: string;
  onSelect: (index: number) => void;
  options: CompassPromptMenuOption[];
}

export const CompassPromptMenu = forwardRef<
  CompassPromptMenuHandle,
  CompassPromptMenuProps
>(({ label, onSelect, options }, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useImperativeHandle(
    ref,
    () => ({
      moveDown: () => setActiveIndex(index => (index + 1) % options.length),
      moveUp: () =>
        setActiveIndex(index => (index - 1 + options.length) % options.length),
      selectActive: () => onSelect(activeIndex),
    }),
    [activeIndex, onSelect, options.length],
  );

  return (
    <Box
      className="absolute top-full z-10 mt-1 max-h-80 w-80 select-none overflow-y-auto p-1"
      data-testid="ai-copilot-compass-prompt-menu"
    >
      {label && (
        <div className="px-2 pb-1 pt-2">
          <Typography
            variant="label-xs"
            color="secondary"
            className="block lowercase first-letter:uppercase"
          >
            {label}
          </Typography>
        </div>
      )}
      {options.map((option, index) => (
        <SuggestionMenuRow
          key={option.id}
          item={{ id: option.id, label: option.label, visual: OPTION_ICON }}
          isActive={index === activeIndex}
          rowRef={index === activeIndex ? activeItemRef : undefined}
          onHover={() => setActiveIndex(index)}
          onSelect={() => onSelect(index)}
        />
      ))}
    </Box>
  );
});

CompassPromptMenu.displayName = "CompassPromptMenu";
